import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, HardDrive, Zap, Gauge, Archive } from 'lucide-react';
import { useListStorageClasses, useCreateVolume } from '@/service/gpu-instance/volume-hook';
import type { AccessMode, VolumeMode, CreateVolumePayload, StorageClass } from '@/service/gpu-instance/volume-type';
import { toast } from 'sonner';

interface CreateVolumeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const accessModeOptions: { value: AccessMode; label: string; description: string }[] = [
  { value: 'ReadWriteOnce', label: 'RWO', description: 'Read/Write by single node' },
  { value: 'ReadWriteMany', label: 'RWX', description: 'Read/Write by many nodes' },
  { value: 'ReadOnlyMany', label: 'ROX', description: 'Read-only by many nodes' },
  { value: 'ReadWriteOncePod', label: 'RWOP', description: 'Read/Write by single pod' },
];

const volumeModeOptions: { value: VolumeMode; label: string; description: string }[] = [
  { value: 'Filesystem', label: 'Filesystem', description: 'Mounted as filesystem with ext4/xfs' },
  { value: 'Block', label: 'Block', description: 'Raw block device for databases' },
];

const storageClassIcons = {
  'standard-ssd': HardDrive,
  'premium-ssd': Zap,
  'nvme': Gauge,
  'archive-hdd': Archive,
};

export const CreateVolumeDialog = ({ open, onOpenChange, onSuccess }: CreateVolumeDialogProps) => {
  const [name, setName] = useState('');
  const [size, setSize] = useState(100);
  const [storageClassId, setStorageClassId] = useState('');
  const [accessMode, setAccessMode] = useState<AccessMode>('ReadWriteOnce');
  const [volumeMode, setVolumeMode] = useState<VolumeMode>('Filesystem');
  const [encrypted, setEncrypted] = useState(true);

  const { data: storageClasses = [], isLoading: loadingStorageClasses } = useListStorageClasses();
  const createVolume = useCreateVolume();

  const selectedStorageClass = storageClasses.find((sc) => sc.id === storageClassId);
  const estimatedCost = selectedStorageClass ? (size * selectedStorageClass.pricePerGBMonth).toFixed(2) : '0.00';

  const validateName = (value: string) => {
    return /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(value);
  };

  const isValid = name.length >= 1 && validateName(name) && storageClassId && size >= 50;

  const handleCreate = async () => {
    if (!isValid) return;

    const payload: CreateVolumePayload = {
      name,
      size,
      storageClassId,
      accessMode,
      volumeMode,
      encrypted,
    };

    try {
      await createVolume.mutateAsync(payload);
      toast.success('Volume creation initiated', {
        description: `Volume "${name}" is being provisioned.`,
      });
      onOpenChange(false);
      onSuccess?.();
      // Reset form
      setName('');
      setSize(100);
      setStorageClassId('');
      setAccessMode('ReadWriteOnce');
      setVolumeMode('Filesystem');
      setEncrypted(true);
    } catch {
      toast.error('Failed to create volume');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Volume</DialogTitle>
          <DialogDescription>
            Create a persistent volume for your GPU instances. Volumes are backed by NetApp Trident.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="volume-name">Volume Name</Label>
            <Input
              id="volume-name"
              placeholder="my-volume-name"
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase())}
              className={name && !validateName(name) ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers, and dashes only. Must start and end with alphanumeric.
            </p>
          </div>

          {/* Size */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Volume Size</Label>
              <span className="text-sm font-medium">{size} GB</span>
            </div>
            <Slider
              value={[size]}
              onValueChange={([value]) => setSize(value)}
              min={50}
              max={10000}
              step={50}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>50 GB</span>
              <span>10,000 GB</span>
            </div>
          </div>

          {/* Storage Class */}
          <div className="space-y-3">
            <Label>Storage Class</Label>
            {loadingStorageClasses ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading storage classes...
              </div>
            ) : (
              <RadioGroup
                value={storageClassId}
                onValueChange={setStorageClassId}
                className="grid grid-cols-1 gap-3 sm:grid-cols-2"
              >
                {storageClasses.map((sc) => {
                  const Icon = storageClassIcons[sc.tier] || HardDrive;
                  return (
                    <label
                      key={sc.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-accent ${
                        storageClassId === sc.id ? 'border-primary bg-accent' : ''
                      }`}
                    >
                      <RadioGroupItem value={sc.id} className="mt-1" />
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <span className="font-medium">{sc.displayName}</span>
                          {sc.isDefault && (
                            <Badge variant="secondary" className="text-xs">
                              Default
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{sc.description}</p>
                        <div className="flex items-center gap-3 text-xs">
                          <span>IOPS: {sc.iopsRange.min.toLocaleString()}-{sc.iopsRange.max.toLocaleString()}</span>
                          <span>|</span>
                          <span>${sc.pricePerGBMonth}/GB/mo</span>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            )}
          </div>

          {/* Access Mode */}
          <div className="space-y-3">
            <Label>Access Mode</Label>
            <Select value={accessMode} onValueChange={(v) => setAccessMode(v as AccessMode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {accessModeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{option.label}</span>
                      <span className="text-muted-foreground">- {option.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Volume Mode */}
          <div className="space-y-3">
            <Label>Volume Mode</Label>
            <RadioGroup
              value={volumeMode}
              onValueChange={(v) => setVolumeMode(v as VolumeMode)}
              className="flex gap-4"
            >
              {volumeModeOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition-colors hover:bg-accent ${
                    volumeMode === option.value ? 'border-primary bg-accent' : ''
                  }`}
                >
                  <RadioGroupItem value={option.value} />
                  <div>
                    <span className="font-medium">{option.label}</span>
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Encryption */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="encryption">Enable Encryption</Label>
              <p className="text-xs text-muted-foreground">
                Encrypt volume data at rest using AES-256
              </p>
            </div>
            <Switch
              id="encryption"
              checked={encrypted}
              onCheckedChange={setEncrypted}
            />
          </div>

          {/* Cost Estimate */}
          {selectedStorageClass && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Estimated Monthly Cost</span>
                <span className="text-lg font-bold">${estimatedCost}/mo</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {size} GB x ${selectedStorageClass.pricePerGBMonth}/GB = ${estimatedCost}/month
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!isValid || createVolume.isPending}>
            {createVolume.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Volume
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateVolumeDialog;
